import { apiFetch, apiGet, apiSend } from "../../lib/api";

export type FoodDashboard = {
  totalIngredients: number;
  totalMenuItems: number;
  totalInvoices: number;
  menuItemsOverTarget: number;
  /** Active items with an ingredient that has no usable cost. */
  menuItemsIncompleteCost?: number;
  averageFoodCostPct: number | null;
  recentInvoices: Array<{
    id: number;
    invoiceNumber: string | null;
    invoiceDate: string;
    totalAmount: number | null;
    vendorName: string;
  }>;
  priceAlerts: Array<{
    ingredientId: number;
    ingredientName: string;
    previousCost: number;
    currentCost: number;
    changePercent: number;
  }>;
};

/** A recipe line with no usable cost, and why. */
export type FoodUnpricedIngredient = {
  ingredientId: number;
  name: string;
  reason: "no_cost" | "unit_mismatch";
  recipeUnit: string;
  costUnit: string | null;
};

export type FoodIngredient = {
  id: number;
  name: string;
  category: string;
  standardUnit: string;
  costOverride: number | null;
  isActive: boolean;
  isBatch: boolean;
  batchYieldQuantity: number | null;
  batchYieldUnit: string | null;
  createdAt: string;
  updatedAt: string;
  latestCost: number | null;
  latestCostUnit: string | null;
  latestCostDate: string | null;
  latestCostSource: string | null;
  /** Cost of one `standardUnit`. Null when no cost converts to that unit. */
  costPerUnit?: number | null;
  costPerUnitSource?: "batch" | "invoice" | "override" | null;
  /** Newest invoice line as billed. */
  latestLineTotal?: number | null;
  latestLineQuantity?: number | null;
  latestLineUnit?: string | null;
  lineItemCount: number;
  menuItemCount: number;
};

export type FoodMenuItem = {
  id: number;
  name: string;
  category: string;
  currentPrice: number | null;
  costOverride: number | null;
  isActive: boolean;
  ingredientCount: number;
  /** Sum of the recipe lines, ignoring the override. Null when nothing could be priced. */
  recipeCost?: number | null;
  /** False when some recipe line has no usable cost, so recipeCost is too low. */
  recipeCostComplete?: boolean;
  unpricedIngredientCount?: number;
  unpricedIngredients?: FoodUnpricedIngredient[];
};

export type FoodInvoice = {
  id: number;
  vendorName: string;
  invoiceNumber: string | null;
  invoiceDate: string;
  totalAmount: number | null;
  lineItemCount: number;
};

export type FoodCategory = {
  id: number;
  name: string;
  type: "ingredient" | "menu";
  isActive: boolean;
  linkedCount: number;
};

export type FoodMenuAnalysisItem = {
  menuItemId: number;
  name: string;
  category: string;
  currentPrice: number | null;
  /** Null when no ingredient could be priced. */
  totalCost: number | null;
  /** False when an ingredient has no usable cost, so totalCost is too low. */
  costComplete?: boolean;
  unpricedIngredientCount?: number;
  unpricedIngredients?: FoodUnpricedIngredient[];
  foodCostPct: number | null;
  suggestedPrice: number | null;
  status: "green" | "yellow" | "red" | "unknown";
};

export type FoodMenuAnalysis = {
  menuItems: FoodMenuAnalysisItem[];
};

export type FoodIngredientDetail = {
  id: number;
  name: string;
  category: string;
  standardUnit: string;
  costOverride: number | null;
  isActive: boolean;
  isBatch: boolean;
  batchYieldQuantity: number | null;
  batchYieldUnit: string | null;
  /** Calculated cost of one `standardUnit`. Null when it can't be priced. */
  costPerUnit?: number | null;
  costPerUnitSource?: "batch" | "invoice" | "override" | null;
  costHistory: Array<{
    id: number;
    date: string;
    vendor: string;
    invoiceId: number;
    invoiceNumber: string | null;
    quantity: number;
    unit: string;
    unitCost: number;
    totalCost: number;
  }>;
  menuItems: Array<{
    id: number;
    name: string;
    category: string;
    currentPrice: number | null;
    isActive: boolean;
    quantity: number;
    unit: string;
  }>;
  batchComponents: Array<{
    id: number;
    ingredientId: number;
    ingredientName: string;
    ingredientStandardUnit: string;
    ingredientCategory: string;
    quantity: number;
    unit: string;
  }>;
};

export type FoodMenuDetail = {
  id: number;
  name: string;
  category: string;
  currentPrice: number | null;
  costOverride: number | null;
  isActive: boolean;
  ingredients: Array<{
    ingredientId: number;
    quantity: number;
    unit: string;
    ingredient: {
      id: number;
      name: string;
      category: string;
      standardUnit: string;
    };
  }>;
  cost: {
    totalCost: number | null;
    costComplete?: boolean;
    unpricedIngredientCount?: number;
    unpricedIngredients?: FoodUnpricedIngredient[];
    foodCostPct: number | null;
    suggestedPrice: number | null;
    status: "green" | "yellow" | "red" | "unknown";
  };
};

export type FoodInvoiceDetail = {
  id: number;
  vendor: { id: number; name: string };
  invoiceNumber: string | null;
  invoiceDate: string;
  totalAmount: number | null;
  lineItems: Array<{
    id: number;
    description: string;
    quantity: number;
    unit: string;
    unitCost: number;
    totalCost: number;
    ingredient: { id: number; name: string } | null;
  }>;
};

export type FoodVendor = { id: number; name: string };

export type FoodExtractedInvoice = {
  vendorName: string;
  invoiceNumber: string | null;
  invoiceDate: string;
  totalAmount: number | null;
  lineItems: Array<{
    description: string;
    quantity: number;
    unit: string;
    unitCost: number;
    totalCost: number;
    /** Looks like a subtotal, tax, total or payment line; review starts it excluded. */
    excluded?: boolean;
  }>;
};

export type FoodMappingSuggestion = {
  index: number;
  matchType: "exact" | "fuzzy";
  confidence: number;
  matchedDescription: string;
  quantity: number;
  unit: string;
  ingredientId: number | null;
};

export function fetchFoodDashboard() {
  return apiGet<FoodDashboard>("/food/dashboard");
}

export function fetchFoodIngredients() {
  return apiGet<FoodIngredient[]>("/food/ingredients");
}

export function createFoodIngredient(payload: {
  name: string;
  category: string;
  standardUnit: string;
  costOverride?: number | null;
}) {
  return apiSend<FoodIngredient>("/food/ingredients", "POST", payload);
}

export function fetchFoodMenuItems() {
  return apiGet<FoodMenuItem[]>("/food/menu");
}

export function fetchFoodInvoices() {
  return apiGet<FoodInvoice[]>("/food/invoices");
}

export function fetchFoodCategories() {
  return apiGet<FoodCategory[]>("/food/categories?status=all");
}

export function createFoodCategory(payload: { name: string; type: "ingredient" | "menu" }) {
  return apiSend<FoodCategory>("/food/categories", "POST", payload);
}

export function updateFoodCategory(
  id: number,
  payload: { name?: string; isActive?: boolean }
) {
  return apiSend<FoodCategory>(`/food/categories/${id}`, "PUT", payload);
}

export function fetchFoodMenuAnalysis() {
  return apiGet<FoodMenuAnalysis>("/food/menu/analysis");
}

export function fetchFoodIngredientDetail(id: number) {
  return apiGet<FoodIngredientDetail>(`/food/ingredients/${id}`);
}

export function fetchFoodMenuDetail(id: number) {
  return apiGet<FoodMenuDetail>(`/food/menu/${id}`);
}

export function fetchFoodInvoiceDetail(id: number) {
  return apiGet<FoodInvoiceDetail>(`/food/invoices/${id}`);
}

export function updateFoodInvoice(
  id: number,
  payload: {
    vendorId?: number;
    invoiceNumber?: string | null;
    invoiceDate?: string;
    totalAmount?: number | null;
  },
) {
  return apiSend<FoodInvoiceDetail>(`/food/invoices/${id}`, "PATCH", payload);
}

export type FoodInvoiceLineInput = {
  description: string;
  quantity: number;
  unit: string;
  unitCost: number;
  totalCost: number;
  ingredientId: number | null;
};

/** Adding, deleting or re-pricing a line resets the invoice total to the sum of its lines. */
export function addFoodInvoiceLine(invoiceId: number, payload: FoodInvoiceLineInput) {
  return apiSend<{ id: number; invoiceTotal: number | null }>(
    `/food/invoices/${invoiceId}/line-items`,
    "POST",
    payload,
  );
}

export function updateFoodInvoiceLine(lineItemId: number, payload: Partial<FoodInvoiceLineInput>) {
  return apiSend<{ id: number; invoiceTotal: number | null }>(
    `/food/invoices/line-items/${lineItemId}`,
    "PATCH",
    payload,
  );
}

export function deleteFoodInvoiceLine(lineItemId: number) {
  return apiSend<{ success: boolean; invoiceTotal: number | null }>(
    `/food/invoices/line-items/${lineItemId}`,
    "DELETE",
  );
}

export function updateFoodIngredient(
  id: number,
  payload: {
    name?: string;
    category?: string;
    standardUnit?: string;
    costOverride?: number | null;
    isActive?: boolean;
    isBatch?: boolean;
    batchYieldQuantity?: number | null;
    batchYieldUnit?: string | null;
    batchComponents?: Array<{
      ingredientId: number;
      quantity: number;
      unit: string;
    }>;
  }
) {
  return apiSend<FoodIngredient>(`/food/ingredients/${id}`, "PUT", payload);
}

export function createFoodMenuItem(payload: {
  name: string;
  category: string;
  currentPrice?: number | null;
  costOverride?: number | null;
  ingredients?: Array<{
    ingredientId: number;
    quantity: number;
    unit: string;
  }>;
}) {
  return apiSend<FoodMenuItem>("/food/menu", "POST", payload);
}

export function updateFoodMenuItem(
  id: number,
  payload: {
    name?: string;
    category?: string;
    currentPrice?: number | null;
    costOverride?: number | null;
    isActive?: boolean;
    ingredients?: Array<{
      ingredientId: number;
      quantity: number;
      unit: string;
    }>;
  }
) {
  return apiSend<FoodMenuItem>(`/food/menu/${id}`, "PUT", payload);
}

export function fetchFoodVendors() {
  return apiGet<FoodVendor[]>("/food/vendors");
}

export async function uploadFoodInvoiceFile(file: File): Promise<{ filePath: string; fileName: string }> {
  const form = new FormData();
  form.set("file", file);
  const r = await apiFetch("/food/invoices/upload", {
    method: "POST",
    body: form,
  });
  if (!r.ok) throw new Error(`${r.status}: ${await r.text()}`);
  return (await r.json()) as { filePath: string; fileName: string };
}

export function extractFoodInvoice(filePath: string) {
  return apiSend<FoodExtractedInvoice>("/food/invoices/extract", "POST", { filePath });
}

export function createFoodVendor(name: string) {
  return apiSend<FoodVendor>("/food/vendors", "POST", { name });
}

/**
 * `lines` (same order as `descriptions`) lets the server scale each past
 * usable quantity to this line's case count, e.g. 2 cases of tenders → 20 lb.
 */
export function suggestFoodInvoiceMappings(
  vendorId: number,
  descriptions: string[],
  lines?: Array<{ unitCost: number; totalCost: number }>,
) {
  return apiSend<{ suggestions: FoodMappingSuggestion[] }>(
    "/food/invoices/mappings/suggest",
    "POST",
    { vendorId, descriptions, lines }
  );
}

export function createFoodInvoice(payload: {
  vendorId: number;
  invoiceNumber: string | null;
  invoiceDate: string;
  totalAmount: number | null;
  filePath: string | null;
  lineItems: Array<{
    description: string;
    quantity: number;
    unit: string;
    unitCost: number;
    totalCost: number;
    ingredientId: number | null;
  }>;
}) {
  return apiSend<{ id: number }>("/food/invoices", "POST", payload);
}
